import React, { useMemo } from 'react';
import classNames from 'classnames';
import './MobileRequestStatusTabs.scss';
import { prefixClass } from 'lib/utils';
import { FilterProps } from 'presentation/components/Queue/utils/filterAndSearchUtils';
import { QueueLabelMap } from 'presentation/components/Queue/utils/askRequestUtils';
import { useAuthContext } from 'context/useAuthContext';
import { HoneAbilityActions, HoneAbilitySubjects } from '@hone-automation/common';

export type ActiveFilterType = 'Blocker' | FilterProps['status'];

interface MobileRequestStatusTabsProps {
  activeFilter: ActiveFilterType;
  onFilterChange: (filter: ActiveFilterType) => void;
  hasBlockers: boolean; // To conditionally show the Blocker tab
}

const TABS: { label: string; filter: ActiveFilterType; userType: string }[] = [
  { label: QueueLabelMap.BLOCKERS, filter: 'Blocker', userType: 'Customer' },
  { label: QueueLabelMap.FOR_ME, filter: 'Open', userType: 'Customer' },
  { label: QueueLabelMap.KITCHEN_SYNC, filter: 'In Progress', userType: 'Customer' },
  { label: QueueLabelMap.CLOSED, filter: 'Closed', userType: 'Customer' },
  { label: QueueLabelMap.BLOCKERS, filter: 'Blocker', userType: 'Bookkeeper' },
  { label: QueueLabelMap.FOR_ME, filter: 'Open', userType: 'Bookkeeper' },
  { label: QueueLabelMap.FOR_CLIENTS, filter: 'In Progress', userType: 'Bookkeeper' },
  { label: QueueLabelMap.CLOSED, filter: 'Closed', userType: 'Bookkeeper' },
];

const MobileRequestStatusTabs: React.FC<MobileRequestStatusTabsProps> = ({
  activeFilter,
  onFilterChange,
  hasBlockers,
}) => {
  const { currentLocationAbility } = useAuthContext();
  const canManageQueue = currentLocationAbility.can(HoneAbilityActions.manage, HoneAbilitySubjects.Queue);
  const prefix = prefixClass('mobile-request-status-tabs');

  // Map filter values to CSS class names
  const filterClassMap: Record<ActiveFilterType, string> = {
    Blocker: 'blocker',
    Open: 'todo',
    'In Progress': 'in-progress',
    Closed: 'resolved',
  };

  const availableTabs = useMemo(() => {
    const tabsPerUser = canManageQueue
      ? TABS.filter(tab => tab.userType === 'Bookkeeper')
      : TABS.filter(tab => tab.userType === 'Customer');
    if (hasBlockers) {
      return tabsPerUser;
    }
    return tabsPerUser.filter(tab => tab.filter !== 'Blocker');
  }, [hasBlockers, canManageQueue]);

  return (
    <div className={prefix()}>
      {availableTabs.map(tab => (
        <button
          key={tab.filter}
          type="button"
          className={classNames(prefix('tab'), {
            [prefix('tab--active')]: activeFilter === tab.filter,
            // Add specific class only when active
            [filterClassMap[tab.filter]]: activeFilter === tab.filter,
          })}
          onClick={() => onFilterChange(tab.filter)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export default MobileRequestStatusTabs;
